<template>
  <empty-layout :enable-scroll-event="true">
    <div
      class="min-h-screen lg:flex-1 bg-primary px-[24px] pt-[calc(env(safe-area-inset-top,0px)_+_24px)] pb-[123px] md:px-[56px] md:pt-[calc(env(safe-area-inset-top,0px)_+_32px)] md:pb-[138px]"
    >
      <div class="mx-auto w-full max-w-[936px]">
        <div class="mb-[24px]">
          <div class="flex w-full items-center justify-between gap-[12px]">
            <BackButton
              fallback-route="/service-highlights"
              :custom-back="handleBack"
              compact
              class="shrink-0"
            />
            <div class="flex min-w-0 flex-1 justify-end">
              <ShareAccessCodeSection
                v-if="shouldShowAccessCodeCopy"
                :loading="isCopyingPasscode"
                class="max-w-full"
                @copy="copyPasscode"
              />
            </div>
          </div>
        </div>
        <section class="pb-6">
          <div class="mb-[16px] flex flex-col gap-[16px] md:flex-row md:items-center md:gap-[24px]">
            <div class="flex items-center gap-[8px] md:gap-[12px]">
              <h1 class="shrink-0 text-[1.5rem] font-black tracking-[-0.03em] text-white">
                {{ pageTitle }}
              </h1>
            </div>
          </div>

          <div ref="stepContent">
            <!-- Initial loading only covers the replay body; controls stay put during swaps. -->
            <div v-if="isInitialLoading" class="flex items-center justify-center py-[80px]">
              <DsSpinner size="lg" label="Loading service replays" />
            </div>
            <div v-else-if="hasAccessCheckError" class="max-w-[420px]">
              <p class="pb-[16px] text-[1.125rem] font-bold leading-[1.5rem] text-white">
                Service Replay is temporarily unavailable
              </p>
              <p class="text-[1rem] leading-[1.5rem] text-light">
                We could not check your access. Please try again.
              </p>
              <DsButton
                variant="bordered"
                theme="dark"
                shape="pill"
                class="mt-[24px]"
                @click="retryAccessCheck"
              >
                Try again
              </DsButton>
            </div>
            <div v-else-if="showPasscodeGate" class="max-w-[420px]">
              <p class="pb-[16px] text-[1.125rem] font-bold leading-[1.5rem] text-white">
                Please enter passcode
              </p>
              <PasscodeVerificationField
                v-model="passcodeField"
                placeholder="Passcode"
                button-label="Enter"
                :has-error="passcodeFailed"
                :error="passcodeFailed ? 'Incorrect code, please try again.' : ''"
                @submit="checkPasscode"
              />
              <p class="pt-[32px] text-[1.125rem] leading-[1.5rem] text-white">
                Need help? Ask your Connect Group Leader for the passcode!
              </p>
            </div>
            <div v-else-if="isDateSelectionStep && weekendsWithReplays.length > 0">
              <p class="text-[1rem] font-bold leading-[1.5rem] text-white">Select a service date</p>
              <div class="mt-[12px] flex flex-col gap-[10px]">
                <button
                  v-for="weekend in weekendsWithReplays"
                  :key="weekend.id"
                  type="button"
                  data-service-replay-date-option="true"
                  class="flex w-full items-center justify-between gap-[12px] rounded-[8px] border border-solid border-light bg-transparent px-[16px] py-[14px] text-left transition-colors active:bg-grey-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  @click="openWeekend(weekend)"
                >
                  <span class="min-w-0">
                    <span class="block truncate text-[1rem] font-bold leading-[1.5rem] text-white">
                      {{ weekend.WeekendDates }}
                    </span>
                    <span
                      class="mt-[2px] block text-[0.75rem] font-medium leading-[1rem] text-light"
                    >
                      {{ getWeekendReplayCountLabel(weekend) }}
                    </span>
                  </span>
                  <DesignSystemIcon
                    name="chevron-right"
                    size="md"
                    class="h-5 w-5 shrink-0 text-white"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
            <div v-else-if="hasCurrentReplay">
              <div v-if="hasReplayControls" class="mt-[12px] flex flex-row flex-wrap gap-[12px]">
                <DsButton
                  v-for="replay in currentWeekendReplays"
                  :key="replay.id"
                  size="48"
                  shape="pill"
                  theme="dark"
                  variant="bordered"
                  :selected="isCurrentReplay(replay)"
                  :class="[
                    'max-w-[220px] flex-shrink-0 truncate',
                    isCurrentReplay(replay)
                      ? '!border-prime-accent !bg-prime-accent !text-prime-ink focus:!ring-0 focus:!ring-offset-0'
                      : '',
                  ]"
                  :title="replay.Title"
                  data-service-replay-service-option="true"
                  @click="selectReplay(replay)"
                >
                  {{ getReplayPillName(replay) }}
                </DsButton>
              </div>

              <div ref="replayContent" class="mt-[16px]">
                <!-- Player -->
                <div class="w-full">
                  <mux-player
                    v-if="isCurrentReplayMux"
                    class="block w-full overflow-hidden rounded-[8px] bg-grey-800"
                    :playback-id="currentReplayUrl"
                    :metadata-video-title="currentReplayHeading"
                    :metadata-viewer-user-id="memberId"
                    primary-color="#ffffff"
                    secondary-color="#000000"
                    @play="trackReplayPlay"
                  ></mux-player>
                  <div
                    v-else-if="currentReplayEmbedUrl"
                    class="relative w-full overflow-hidden rounded-[8px] bg-grey-800 pt-[56.25%]"
                  >
                    <iframe
                      class="absolute top-0 left-0 h-full w-full"
                      :src="currentReplayEmbedUrl"
                      :title="currentReplay.VideoTitle"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowfullscreen
                      referrerpolicy="strict-origin-when-cross-origin"
                    >
                    </iframe>
                  </div>
                  <video
                    v-else-if="currentReplayVideoUrl"
                    class="block w-full overflow-hidden rounded-[8px] bg-grey-800"
                    :src="currentReplayVideoUrl"
                    :title="currentReplay.VideoTitle"
                    preload="metadata"
                    controls
                    controlsList="nodownload noremoteplayback"
                    playsinline
                    disablePictureInPicture
                    @play="trackReplayPlay"
                    @contextmenu.prevent
                  ></video>
                  <div
                    v-else
                    class="flex aspect-video w-full items-center justify-center rounded-[8px] bg-grey-800 px-[24px] text-center"
                  >
                    <p class="text-[1rem] font-medium leading-[1.5rem] text-light">
                      This replay is unavailable right now.
                    </p>
                  </div>
                </div>

                <div class="mt-[10px]">
                  <h2 class="text-[1.125rem] font-bold leading-[1.5rem] text-white">
                    {{ currentReplay.VideoTitle }}
                  </h2>
                </div>
              </div>
            </div>
            <div
              v-else
              class="flex flex-col items-center justify-center gap-[8px] py-[80px] text-center"
            >
              <h2 class="text-[1.125rem] font-bold leading-[1.5rem] text-white">No replays yet</h2>
              <p class="max-w-[320px] text-[1rem] font-medium leading-[1.5rem] text-light">
                Service replays appear here after the weekend services. Check back soon.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  </empty-layout>
</template>

<script>
import { mapState } from 'vuex';
import BackButton from '@/components/common/BackButton.vue';
import ShareAccessCodeSection from '@/components/ServiceReplayPage/ShareAccessCodeSection.vue';
import PasscodeVerificationField from '@/components/ServiceReplayPage/PasscodeVerificationField.vue';
import {
  Button as DsButton,
  Spinner as DsSpinner,
  createToastBottomNavPositionAnchor,
  Icon as DesignSystemIcon,
} from '@team-hogc/hogc-one-design-system';
import '@mux/mux-player';
import { trackButtonClick } from '@/utils/platform-check';
import { getVimeoEmbedUrl } from '@/utils/vimeo';
import { createAnimation, toastController } from '@ionic/vue';
import { triggerNotification } from '@/utils/haptics';
import {
  trackServiceReplayOpened,
  trackServiceReplayPlayed,
  trackServiceReplaySelected,
} from '@/utils/analytics';
import { getAnimationPlatform, getTimingForPlatform } from '@/utils/animations';

const BOTTOM_NAV_HEIGHT_PX = 68;
const COPY_TOAST_BOTTOM_NAV_SELECTOR = '[data-ds-bottom-nav]';
const COPY_TOAST_NAV_GAP_PX = 16;
const COPY_TOAST_DEFAULT_NAV_CLEARANCE_PX = BOTTOM_NAV_HEIGHT_PX;
const STEP_TRANSITION_DISTANCE_PX = 24;
const YOUTUBE_VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

export default {
  data() {
    return {
      currentReplay: {},
      currentWeekend: {},
      historyLenOnEnter: 0,
      historyLenBeforeWeekendSelection: null,
      hasAccessCheckError: false,
      isResolvingInitialData: true,
      isCopyingPasscode: false,
      lastTrackedReplayOpenId: null,
      pendingStepAnimation: null,
      passcodeFetchPromise: null,
      passcodeFailed: false,
      passcodeField: '',
      showPasscodeGate: false,
    };
  },
  components: {
    DsButton,
    DsSpinner,
    BackButton,
    ShareAccessCodeSection,
    PasscodeVerificationField,
    DesignSystemIcon,
  },
  computed: {
    ...mapState('serviceReplay', {
      isLoading: state => state.isLoading,
      weekends: state => state.weekends,
      hasEnteredPassword: state => state.hasEnteredPassword,
      passcode: state => state.passcode,
      serviceReplayAccess: state => state.access,
    }),
    ...mapState('profile', {
      isLeader: state => state.isLeader,
      memberId: state => state.id,
    }),
    currentWeekendReplays() {
      return this.currentWeekend?.hogc_one_replays ?? [];
    },
    pageTitle() {
      return this.currentWeekend?.WeekendDates || 'Service Replay';
    },
    currentReplayHeading() {
      return this.getReplayPillName(this.currentReplay);
    },
    currentReplayUrl() {
      return this.currentReplay?.VideoUrl?.trim() || '';
    },
    isCurrentReplayMux() {
      return this.isReplayMux(this.currentReplay);
    },
    currentReplayEmbedUrl() {
      if (this.isCurrentReplayMux) return '';
      return this.getEmbeddableReplayUrl(this.currentReplayUrl);
    },
    currentReplayVideoUrl() {
      if (this.isCurrentReplayMux || this.currentReplayEmbedUrl) return '';
      return this.currentReplayUrl;
    },
    weekendsWithReplays() {
      return (this.weekends ?? []).filter(weekend => weekend.hogc_one_replays?.length > 0);
    },
    hasRouteSelection() {
      const { replay, weekend } = this.$route.query ?? {};
      return Boolean(replay || weekend);
    },
    isDateSelectionStep() {
      return !this.hasRouteSelection;
    },
    isInitialLoading() {
      if (this.isResolvingInitialData) return true;

      return this.isLoading && this.weekendsWithReplays.length === 0 && !this.hasCurrentReplay;
    },
    hasCurrentReplay() {
      return !!this.currentReplay?.id;
    },
    canSharePasscode() {
      return Boolean(this.isLeader || this.serviceReplayAccess?.canSharePasscode);
    },
    shouldShowAccessCodeCopy() {
      return Boolean(
        this.canSharePasscode &&
          !this.isInitialLoading &&
          !this.hasAccessCheckError &&
          !this.showPasscodeGate &&
          (this.isDateSelectionStep || this.hasCurrentReplay),
      );
    },
    hasReplayControls() {
      return this.currentWeekendReplays.length > 1;
    },
  },
  watch: {
    '$route.query': {
      async handler() {
        if (this.weekends?.length > 0) {
          this.resolveFromQuery();
          await this.playPendingStepAnimation();
        }
      },
      deep: true,
    },
    async 'currentReplay.id'(newId, oldId) {
      // Skip first paint (no prior id) — only animate on user-driven swaps
      if (!newId || !oldId || newId === oldId) return;
      const el = this.$refs.replayContent;
      if (!el) return;
      el.style.opacity = '0';
      await this.$nextTick();
      await createAnimation().addElement(el).duration(250).fromTo('opacity', '0', '1').play();
    },
  },
  methods: {
    handleBack() {
      if (!this.showPasscodeGate && this.hasRouteSelection) {
        this.goToDateSelection();
        return;
      }

      // Iframe navigations (Crisp chat, GTM, video embeds) bump
      // window.history.length without calling pushState. A plain go(-1)
      // lands on those ghost entries — Vue Router sees no state.position
      // change and doesn't navigate, so the back tap appears frozen.
      // Snapshot history.length on enter, step past every accumulated ghost
      // plus one more to exit the page.
      const ghosts = Math.max(0, window.history.length - this.historyLenOnEnter);
      if (this.historyLenOnEnter > 1) {
        window.history.go(-(ghosts + 1));
      } else {
        this.$router.push('/service-highlights');
      }
    },
    requiresPasscodeGate(access = this.serviceReplayAccess) {
      const canBypassPasscode = this.isLeader || access?.canSharePasscode;

      return Boolean(
        access &&
          !canBypassPasscode &&
          (!access.canAccess || access.requiresPasscode) &&
          !this.hasEnteredPassword,
      );
    },
    async checkServiceReplayAccess(access = this.serviceReplayAccess) {
      this.hasAccessCheckError = false;

      if (!access) {
        this.showPasscodeGate = false;
        this.hasAccessCheckError = true;
        return false;
      }

      if (this.requiresPasscodeGate(access)) {
        this.showPasscodeGate = true;
        return false;
      }

      this.showPasscodeGate = false;
      return true;
    },
    async loadServiceReplayContent() {
      await Promise.all([
        this.$store.dispatch('profile/fetchProfile'),
        this.$store.dispatch('serviceReplay/fetchWeekends'),
      ]);

      if (this.canSharePasscode) {
        this.prefetchPasscode().catch(error => {
          console.warn('[ServiceReplay] Passcode prefetch failed:', error);
        });
      }
      this.resolveFromQuery();
    },
    async retryAccessCheck() {
      this.hasAccessCheckError = false;
      this.showPasscodeGate = false;
      this.isResolvingInitialData = true;

      try {
        const [access] = await Promise.all([
          this.$store.dispatch('serviceReplay/fetchAccess'),
          this.$store.dispatch('profile/fetchProfile'),
        ]);
        if (!(await this.checkServiceReplayAccess(access))) {
          return;
        }

        await this.loadServiceReplayContent();
      } finally {
        this.isResolvingInitialData = false;
      }
    },
    async checkPasscode() {
      this.passcodeFailed = false;
      const isValid = await this.$store.dispatch(
        'serviceReplay/verifyPasscode',
        this.passcodeField.trim(),
      );

      if (!isValid) {
        this.passcodeFailed = true;
        return;
      }

      this.showPasscodeGate = false;
      this.isResolvingInitialData = true;

      try {
        if (!this.serviceReplayAccess) {
          await this.$store.dispatch('serviceReplay/fetchAccess');
        }
        await this.loadServiceReplayContent();
      } finally {
        this.isResolvingInitialData = false;
      }
    },
    createCopyToastPositionAnchor() {
      const bottomNav = document.querySelector(COPY_TOAST_BOTTOM_NAV_SELECTOR);

      if (!bottomNav) return undefined;

      return createToastBottomNavPositionAnchor({
        navElement: bottomNav,
        gap: COPY_TOAST_NAV_GAP_PX,
        defaultNavClearance: COPY_TOAST_DEFAULT_NAV_CLEARANCE_PX,
      });
    },
    async showCopyToast(message, variant) {
      const positionAnchor = this.createCopyToastPositionAnchor();
      const toast = await toastController.create({
        message,
        duration: 3000,
        position: 'bottom',
        positionAnchor,
        cssClass: ['ds-toast', `ds-toast--${variant}`],
      });

      if (positionAnchor) {
        toast.onDidDismiss().then(() => positionAnchor.remove());
      }
      await toast.present();
    },
    async copyPasscode() {
      if (this.isCopyingPasscode) return;

      this.isCopyingPasscode = true;

      try {
        const passcode = await this.ensurePasscode();

        if (!passcode) {
          throw new Error('Missing service replay passcode');
        }

        await this.copyTextToClipboard(passcode);
        await triggerNotification('success');
        await this.showCopyToast('Access code copied', 'success');
      } catch (error) {
        console.warn('[ServiceReplay] Copy passcode failed:', error);
        await this.showCopyToast('Failed to copy access code', 'error');
      } finally {
        this.isCopyingPasscode = false;
      }
    },
    async copyTextToClipboard(text) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          return;
        }
      } catch (error) {
        console.warn('[ServiceReplay] Clipboard API unavailable, trying fallback:', error);
      }

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.setAttribute('readonly', '');
      textArea.setAttribute('aria-hidden', 'true');
      textArea.className = 'fixed left-[-9999px] top-0 opacity-0';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);

      try {
        const copied = document.execCommand?.('copy');
        if (!copied) throw new Error('Fallback clipboard copy failed');
      } finally {
        document.body.removeChild(textArea);
      }
    },
    async ensurePasscode() {
      if (this.passcode) return this.passcode;

      await this.prefetchPasscode();

      return this.passcode;
    },
    prefetchPasscode() {
      if (this.passcode) return Promise.resolve(this.passcode);
      if (this.passcodeFetchPromise) return this.passcodeFetchPromise;

      this.passcodeFetchPromise = this.$store
        .dispatch('serviceReplay/fetchPasscode')
        .finally(() => {
          this.passcodeFetchPromise = null;
        });

      return this.passcodeFetchPromise;
    },
    isCurrentWeekend(weekend) {
      return this.currentWeekend?.id === weekend.id;
    },
    isCurrentReplay(replay) {
      return this.currentReplay?.id === replay.id;
    },
    getReplayPillName(replay) {
      if (replay.PillName) return replay.PillName;
      return replay.Title?.split(' - ')[0] || replay.Title || '';
    },
    isReplayMux(replay) {
      return replay?.isMux === true || replay?.isMux === 'true';
    },
    getYouTubeVideoId(url) {
      if (!url) return null;
      if (YOUTUBE_VIDEO_ID_PATTERN.test(url)) return url;

      try {
        const parsedUrl = new URL(url);

        if (parsedUrl.hostname.includes('youtu.be')) {
          return parsedUrl.pathname.split('/').filter(Boolean)[0] || null;
        }

        if (parsedUrl.searchParams.get('v')) {
          return parsedUrl.searchParams.get('v');
        }

        const embedMatch = parsedUrl.pathname.match(/\/embed\/([^/?#]+)/);
        if (embedMatch?.[1]) return embedMatch[1];
      } catch (error) {
        const queryMatch = url.match(/[?&]v=([^?&#]+)/);
        if (queryMatch?.[1]) return queryMatch[1];
      }

      return null;
    },
    getEmbeddableReplayUrl(url) {
      if (!url) return '';

      const youtubeVideoId = this.getYouTubeVideoId(url);
      if (youtubeVideoId) {
        const params = new URLSearchParams({ v: youtubeVideoId, autoplay: '0' });
        const origin = process.env.VUE_APP_APP_DOMAIN || window.location.origin;
        return `${origin}/youtube?${params.toString()}`;
      }

      const vimeoEmbedUrl = getVimeoEmbedUrl(url);
      if (vimeoEmbedUrl) return vimeoEmbedUrl;

      if (/\/embed\/|youtube-nocookie\.com|iframe\.mux\.com/.test(url)) {
        return url;
      }

      return '';
    },
    getWeekendReplayCountLabel(weekend) {
      const count = weekend.hogc_one_replays?.length ?? 0;
      return count === 1 ? '1 service' : `${count} services`;
    },
    clearReplaySelection() {
      this.currentWeekend = {};
      this.currentReplay = {};
      this.lastTrackedReplayOpenId = null;
    },
    resetWeekendHistoryBaseline() {
      if (this.historyLenBeforeWeekendSelection === null) return;

      this.historyLenOnEnter = window.history.length;
      this.historyLenBeforeWeekendSelection = null;
    },
    goToDateSelection() {
      this.pendingStepAnimation = 'back';

      if (
        this.historyLenBeforeWeekendSelection !== null &&
        window.history.length > this.historyLenBeforeWeekendSelection
      ) {
        window.history.go(-(window.history.length - this.historyLenBeforeWeekendSelection));
        return;
      }

      this.$router.replace('/service-replay');
    },
    openWeekend(weekend) {
      if (!weekend?.id) return;
      this.pendingStepAnimation = 'forward';
      this.historyLenBeforeWeekendSelection = window.history.length;
      this.$router.push({
        path: '/service-replay',
        query: { weekend: String(weekend.id) },
      });
    },
    selectWeekend(weekend) {
      if (this.isCurrentWeekend(weekend) && this.hasCurrentReplay) return;
      const firstReplay = weekend.hogc_one_replays?.[0];
      if (!firstReplay) return;
      this.currentWeekend = weekend;
      this.currentReplay = firstReplay;
      trackButtonClick('service-replay-' + weekend.WeekendDates, 'ServiceReplayPage');
      trackServiceReplaySelected({
        ...this.replayAnalyticsPayload(firstReplay, weekend),
        selectionType: 'weekend',
        source: 'service-replay-weekend-selector',
      });
      this.trackCurrentReplayOpened('service-replay-weekend-selector');
    },
    selectReplay(replay, source = 'service-replay-replay-selector') {
      if (this.isCurrentReplay(replay)) return;
      this.currentReplay = replay;
      trackButtonClick('service-replay-' + replay.VideoTitle, 'ServiceReplayPage');
      trackServiceReplaySelected({
        ...this.replayAnalyticsPayload(replay, this.currentWeekend),
        selectionType: 'replay',
        source,
      });
      this.trackCurrentReplayOpened(source);
    },
    async playPendingStepAnimation() {
      const direction = this.pendingStepAnimation;
      if (!direction) return;

      this.pendingStepAnimation = null;
      await this.$nextTick();

      const el = this.$refs.stepContent;
      if (!el) return;

      const distance =
        direction === 'back' ? -STEP_TRANSITION_DISTANCE_PX : STEP_TRANSITION_DISTANCE_PX;
      const startTransform = `translate3d(${distance}px, 0, 0)`;
      const endTransform = 'translate3d(0, 0, 0)';
      const timing = getTimingForPlatform(getAnimationPlatform());

      el.style.opacity = '0';
      el.style.transform = startTransform;
      el.style.willChange = 'opacity, transform';

      try {
        await createAnimation()
          .addElement(el)
          .duration(timing.DURATION)
          .easing(timing.EASING)
          .fromTo('opacity', '0', '1')
          .fromTo('transform', startTransform, endTransform)
          .play();
      } finally {
        el.style.opacity = '';
        el.style.transform = '';
        el.style.willChange = '';
      }
    },
    replayAnalyticsPayload(replay = this.currentReplay, weekend = this.currentWeekend) {
      return {
        replayId: replay?.id,
        title: replay?.VideoTitle || replay?.Title,
        weekendId: weekend?.id,
        weekendLabel: weekend?.WeekendDates,
      };
    },
    trackCurrentReplayOpened(source) {
      if (!this.currentReplay?.id || this.lastTrackedReplayOpenId === this.currentReplay.id) return;

      trackServiceReplayOpened({
        ...this.replayAnalyticsPayload(),
        source,
      });
      this.lastTrackedReplayOpenId = this.currentReplay.id;
    },
    trackReplayPlay() {
      trackServiceReplayPlayed({
        ...this.replayAnalyticsPayload(),
        source: 'service-replay-player',
      });
    },
    resolveFromQuery() {
      if (!this.weekends || this.weekends.length === 0) return false;
      const { weekend: weekendParam, replay: replayParam } = this.$route.query ?? {};
      const weekendsWithReplays = this.weekends.filter(
        weekend => weekend.hogc_one_replays?.length > 0,
      );

      if (weekendsWithReplays.length === 0) {
        this.clearReplaySelection();
        return false;
      }

      if (!replayParam && !weekendParam) {
        this.clearReplaySelection();
        this.resetWeekendHistoryBaseline();
        return true;
      }

      // Priority 1: explicit replay id (auto-resolves parent weekend)
      if (replayParam) {
        const idStr = String(replayParam);
        for (const weekend of weekendsWithReplays) {
          const match = weekend.hogc_one_replays?.find(r => String(r.id) === idStr);
          if (match) {
            this.currentWeekend = weekend;
            this.currentReplay = match;
            this.trackCurrentReplayOpened('service-replay-route-query');
            return true;
          }
        }
      }

      // Priority 2: weekend id only — first replay of that weekend
      if (weekendParam) {
        const idStr = String(weekendParam);
        const weekend = weekendsWithReplays.find(w => String(w.id) === idStr);
        if (weekend) {
          this.selectWeekend(weekend);
          return true;
        }
      }

      this.clearReplaySelection();
      this.resetWeekendHistoryBaseline();
      this.$router?.replace?.('/service-replay');
      return true;
    },
  },
  async ionViewWillEnter() {
    this.isResolvingInitialData = true;
    this.historyLenOnEnter = window.history.length;

    try {
      const [access] = await Promise.all([
        this.$store.dispatch('serviceReplay/fetchAccess'),
        this.$store.dispatch('profile/fetchProfile'),
      ]);
      if (!(await this.checkServiceReplayAccess(access))) {
        return;
      }

      await this.loadServiceReplayContent();
    } finally {
      this.isResolvingInitialData = false;
    }
  },
};
</script>
